/**
 * Custom Connector Builder API Client
 */

import { api } from '@/lib/api-client';
import type {
  CustomConnector,
  CreateConnectorInput,
  UpdateConnectorInput,
  ConnectorResponse,
  ConnectorListResponse,
  ConnectorOperation,
  CreateOperationInput,
  UpdateOperationInput,
  OperationResponse,
  OperationListResponse,
  ConnectorOAuthConfig,
  OAuthConfigInput,
  OAuthConfigResponse,
  TestOperationInput,
  TestRunResponse,
  ImportResponse,
} from './types';

const CONNECTORS_BASE = '/api/v1/connectors';

// ============================================
// Custom Connectors
// ============================================

/**
 * List custom connectors
 */
export async function listConnectors(params?: {
  is_published?: boolean;
  is_public?: boolean;
}): Promise<ConnectorListResponse> {
  const queryParams: Record<string, string> = {};
  if (params?.is_published !== undefined) queryParams.is_published = String(params.is_published);
  if (params?.is_public !== undefined) queryParams.is_public = String(params.is_public);

  return api.get<ConnectorListResponse>(CONNECTORS_BASE, { params: queryParams });
}

/**
 * Get connector by ID
 */
export async function getConnector(id: string): Promise<ConnectorResponse> {
  return api.get<ConnectorResponse>(`${CONNECTORS_BASE}/${id}`);
}

/**
 * Create a new connector
 */
export async function createConnector(input: CreateConnectorInput): Promise<ConnectorResponse> {
  return api.post<ConnectorResponse>(CONNECTORS_BASE, input);
}

/**
 * Update a connector
 */
export async function updateConnector(
  id: string,
  input: UpdateConnectorInput
): Promise<ConnectorResponse> {
  return api.put<ConnectorResponse>(`${CONNECTORS_BASE}/${id}`, input);
}

/**
 * Delete a connector
 */
export async function deleteConnector(id: string): Promise<{ success: boolean }> {
  return api.delete(`${CONNECTORS_BASE}/${id}`);
}

/**
 * Publish a connector
 */
export async function publishConnector(id: string): Promise<ConnectorResponse> {
  return api.post<ConnectorResponse>(`${CONNECTORS_BASE}/${id}/publish`);
}

/**
 * Unpublish a connector
 */
export async function unpublishConnector(id: string): Promise<ConnectorResponse> {
  return api.post<ConnectorResponse>(`${CONNECTORS_BASE}/${id}/unpublish`);
}

// ============================================
// Connector Operations
// ============================================

/**
 * List operations for a connector
 */
export async function listOperations(connectorId: string): Promise<OperationListResponse> {
  return api.get<OperationListResponse>(`${CONNECTORS_BASE}/${connectorId}/operations`);
}

/**
 * Get operation by ID
 */
export async function getOperation(
  connectorId: string,
  operationId: string
): Promise<OperationResponse> {
  return api.get<OperationResponse>(`${CONNECTORS_BASE}/${connectorId}/operations/${operationId}`);
}

/**
 * Create a new operation
 */
export async function createOperation(
  connectorId: string,
  input: CreateOperationInput
): Promise<OperationResponse> {
  return api.post<OperationResponse>(`${CONNECTORS_BASE}/${connectorId}/operations`, input);
}

/**
 * Update an operation
 */
export async function updateOperation(
  connectorId: string,
  operationId: string,
  input: UpdateOperationInput
): Promise<OperationResponse> {
  return api.put<OperationResponse>(
    `${CONNECTORS_BASE}/${connectorId}/operations/${operationId}`,
    input
  );
}

/**
 * Delete an operation
 */
export async function deleteOperation(
  connectorId: string,
  operationId: string
): Promise<{ success: boolean }> {
  return api.delete(`${CONNECTORS_BASE}/${connectorId}/operations/${operationId}`);
}

/**
 * Reorder operations
 */
export async function reorderOperations(
  connectorId: string,
  operationIds: string[]
): Promise<{ success: boolean }> {
  return api.post(`${CONNECTORS_BASE}/${connectorId}/operations/reorder`, {
    operation_ids: operationIds,
  });
}

// ============================================
// OAuth Configuration
// ============================================

/**
 * Get OAuth configuration
 */
export async function getOAuthConfig(connectorId: string): Promise<OAuthConfigResponse> {
  return api.get<OAuthConfigResponse>(`${CONNECTORS_BASE}/${connectorId}/oauth`);
}

/**
 * Save OAuth configuration
 */
export async function saveOAuthConfig(
  connectorId: string,
  config: OAuthConfigInput
): Promise<OAuthConfigResponse> {
  return api.post<OAuthConfigResponse>(`${CONNECTORS_BASE}/${connectorId}/oauth`, config);
}

/**
 * Delete OAuth configuration
 */
export async function deleteOAuthConfig(connectorId: string): Promise<{ success: boolean }> {
  return api.delete(`${CONNECTORS_BASE}/${connectorId}/oauth`);
}

// ============================================
// Testing
// ============================================

/**
 * Test authentication
 */
export async function testAuth(
  connectorId: string,
  credentials: Record<string, string>
): Promise<TestRunResponse> {
  return api.post<TestRunResponse>(`${CONNECTORS_BASE}/${connectorId}/test-auth`, { credentials });
}

/**
 * Test an operation
 */
export async function testOperation(
  connectorId: string,
  operationId: string,
  input: TestOperationInput
): Promise<TestRunResponse> {
  return api.post<TestRunResponse>(
    `${CONNECTORS_BASE}/${connectorId}/operations/${operationId}/test`,
    input
  );
}

/**
 * Run full connector test
 */
export async function testConnector(
  connectorId: string,
  credentials: Record<string, string>
): Promise<TestRunResponse> {
  return api.post<TestRunResponse>(`${CONNECTORS_BASE}/${connectorId}/test`, { credentials });
}

// ============================================
// Import
// ============================================

/**
 * Import from OpenAPI spec
 */
export async function importOpenAPI(
  connectorId: string,
  spec: string,
  format: 'json' | 'yaml' = 'json'
): Promise<ImportResponse> {
  return api.post<ImportResponse>(`${CONNECTORS_BASE}/${connectorId}/import-openapi`, {
    spec,
    format,
  });
}

/**
 * Import from Postman collection
 */
export async function importPostman(
  connectorId: string,
  collection: string
): Promise<ImportResponse> {
  return api.post<ImportResponse>(`${CONNECTORS_BASE}/${connectorId}/import-postman`, {
    collection,
  });
}

// ============================================
// Export all functions
// ============================================

export default {
  // Connectors
  listConnectors,
  getConnector,
  createConnector,
  updateConnector,
  deleteConnector,
  publishConnector,
  unpublishConnector,
  // Operations
  listOperations,
  getOperation,
  createOperation,
  updateOperation,
  deleteOperation,
  reorderOperations,
  // OAuth
  getOAuthConfig,
  saveOAuthConfig,
  deleteOAuthConfig,
  // Testing
  testAuth,
  testOperation,
  testConnector,
  // Import
  importOpenAPI,
  importPostman,
};
